/**
 * All Farm View sorting helpers.
 * Shared by the web and mobile farm view / field list sorting.
 */

/**
 * Normalises a timestamp to a comparable number.
 * Accepts numbers, numeric strings (seconds or ms) and ISO strings.
 * @param {string|number|null|undefined} ts
 * @returns {number} the numeric timestamp, or -1 when it cannot be parsed
 */
var parseSortTimestamp = function (ts) {
  if (ts === null || ts === undefined) {
    return -1;
  }
  if (typeof ts === 'number') {
    return Number.isFinite(ts) ? ts : -1;
  }
  if (typeof ts === 'string') {
    var trimmed = ts.trim();
    if (trimmed.length === 0) {
      return -1;
    }
    // Numeric timestamps (seconds or ms) sometimes come as strings
    var numeric = Number(trimmed);
    if (Number.isFinite(numeric)) {
      return numeric;
    }
    // ISO timestamp
    var parsed = Date.parse(trimmed);
    return Number.isFinite(parsed) ? parsed : -1;
  }
  return -1;
};

/**
 * How long the last *completed* irrigation ran for.
 * A field that never irrigated, or one that is still irrigating (no end
 * timestamp yet), has no duration to sort by.
 *
 * Both timestamps must come from the same source: pairing a live start with a
 * historical end would measure two different irrigation events.
 *
 * @param {string|number|null} startedAt - last irrigation start
 * @param {string|number|null} endedAt - last irrigation end
 * @returns {number|null} duration in the timestamps' own units, or null when unknown
 */
var lastIrrigationDurationValue = function (startedAt, endedAt) {
  var start = parseSortTimestamp(startedAt);
  var end = parseSortTimestamp(endedAt);
  if (start < 0 || end < 0) {
    return null;
  }
  var duration = end - start;
  return duration >= 0 ? duration : null;
};

/**
 * Compares two last-irrigation durations.
 * Fields with no known duration always sink to the bottom, in both directions,
 * so flipping the sort never floats "no data" rows to the top.
 *
 * @param {number|null} aDuration
 * @param {number|null} bDuration
 * @param {boolean} ascending - true for shortest → longest
 * @returns {number}
 */
var compareLastIrrigationDuration = function (aDuration, bDuration, ascending) {
  var aMissing = aDuration === null || aDuration === undefined;
  var bMissing = bDuration === null || bDuration === undefined;
  if (aMissing && bMissing) {
    return 0;
  }
  if (aMissing) {
    return 1;
  }
  if (bMissing) {
    return -1;
  }
  return ascending ? aDuration - bDuration : bDuration - aDuration;
};

module.exports = {
  parseSortTimestamp: parseSortTimestamp,
  lastIrrigationDurationValue: lastIrrigationDurationValue,
  compareLastIrrigationDuration: compareLastIrrigationDuration,
};
